import React from 'react';
import { Box, CTALink, Heading, Inline, Link } from '@99designs/emu';
import { IconLeft1 } from '@99designs/icons';
import { ProductCardGroup, ProductCardProps } from '@99designs/marketing-ui';
import { useTrackLink } from '@99designs/tracking';
import { useOnboarder } from '../components/Onboarder/OnboarderContext';
import { findStepNumber } from '../components/Onboarder/helpers/findStepNumber';
import { BrandingStageStepAnswer } from './BrandingStageStep';
import { IndustryStepAnswer } from './IndustryStep';
import { LogoStepAnswer } from './LogoStep';
import { ProductPreferencesStepAnswer } from './ProductPreferencesStep';
import { PromotionStepAnswer } from './PromotionStep';
import {
    STEP_BRANDING_STAGE,
    STEP_INDUSTRY,
    STEP_LOGO,
    STEP_PRODUCT_PREFERENCES,
    STEP_PROMOTION,
} from './constants';
import { editAnswersClicked } from './utils/tracking';
import { editLinkClicked } from './utils/tracking';
import type { Step } from '../components/Onboarder/Config';
import type { SelectedValues } from './config';
import type { Product } from './types';

type StepAnswers = Record<keyof SelectedValues, React.ReactNode>;

const getAnswers = (
    basePath: string,
    steps: Step<SelectedValues>[],
    data: SelectedValues
): StepAnswers => {
    const getLink = (key: keyof SelectedValues): string => {
        const stepNumber = findStepNumber(steps, key);

        return `${basePath}/${stepNumber}/${key}`;
    };

    const trackingHandler = (key: keyof SelectedValues): void => {
        const stepNumber = findStepNumber(steps, key);

        editLinkClicked(key, stepNumber);
    };

    const onEditLinkClick = (key: keyof SelectedValues): void => {
        trackingHandler(key);
        window.scrollTo(0, 0);
    };

    return {
        [STEP_BRANDING_STAGE]: (
            <BrandingStageStepAnswer
                key={STEP_BRANDING_STAGE}
                data={data}
                link={getLink(STEP_BRANDING_STAGE)}
                onClick={() => onEditLinkClick(STEP_BRANDING_STAGE)}
            />
        ),
        [STEP_INDUSTRY]: (
            <IndustryStepAnswer
                key={STEP_INDUSTRY}
                data={data}
                link={getLink(STEP_INDUSTRY)}
                onClick={() => onEditLinkClick(STEP_INDUSTRY)}
            />
        ),
        [STEP_PROMOTION]: (
            <PromotionStepAnswer
                key={STEP_PROMOTION}
                data={data}
                link={getLink(STEP_PROMOTION)}
                onClick={() => onEditLinkClick(STEP_PROMOTION)}
            />
        ),
        [STEP_LOGO]: (
            <LogoStepAnswer
                key={STEP_LOGO}
                data={data}
                link={getLink(STEP_LOGO)}
                onClick={() => onEditLinkClick(STEP_LOGO)}
            />
        ),
        [STEP_PRODUCT_PREFERENCES]: (
            <ProductPreferencesStepAnswer
                key={STEP_PRODUCT_PREFERENCES}
                data={data}
                link={getLink(STEP_PRODUCT_PREFERENCES)}
                onClick={() => onEditLinkClick(STEP_PRODUCT_PREFERENCES)}
            />
        ),
    };
};

const recommendationOrder = [
    STEP_BRANDING_STAGE,
    STEP_INDUSTRY,
    STEP_PROMOTION,
    STEP_LOGO,
    STEP_PRODUCT_PREFERENCES,
];

interface RecommendationsProps {
    recommendedProducts: Product[];
}

export function Recommendations({ recommendedProducts }: RecommendationsProps) {
    const { data, config, steps } = useOnboarder<SelectedValues>();
    const answers = getAnswers(config.basePath, steps, data);

    const ctaButtonRef = useTrackLink({
        event: 'See all Categories link on Branding Page Clicked',
    });

    const tileTracking = (product: ProductCardProps) => {
        const currentProduct = recommendedProducts.find(
            (recommendedProduct) => recommendedProduct.title === product.title
        );

        return {
            event: 'Product tile on Recommendation page clicked',
            meta: {
                category: currentProduct ? currentProduct?.categoryKey : undefined,
            },
        };
    };

    return (
        <>
            <Box display="flex" justifyContent="center">
                <Box width="100%" maxWidth="1300px" paddingX="2bu" marginTop="2bu">
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        flexDirection={['column', 'row', 'row']}
                        alignItems={['start', 'center', 'center']}
                        paddingBottom="1bu"
                    >
                        <Link
                            href={`${config.basePath}/1/${steps[0].key}`}
                            title={__('Edit your answers')}
                            color="grey700"
                            size="size2"
                            onClick={editAnswersClicked}
                        >
                            <Inline space="0.25bu">
                                <IconLeft1 />
                                {__('Edit your answers')}
                            </Inline>
                        </Link>
                        <Box display={['none', 'block', 'block']}>
                            <CTALink href="/categories" ref={ctaButtonRef}>
                                {__('See all categories')}
                            </CTALink>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box width="100%" role="main">
                <ProductCardGroup products={recommendedProducts} tracking={tileTracking} />
            </Box>
            <Box
                paddingX="2bu"
                marginBottom="2bu"
                justifyContent="start"
                display={['flex', 'none', 'none']}
            >
                <CTALink href="/categories" ref={ctaButtonRef}>
                    {__('See all categories')}
                </CTALink>
            </Box>
            <Box display="flex" justifyContent="center">
                <Box width="100%" maxWidth="1300px" paddingX="2bu" paddingTop="2bu">
                    <Heading accessibilityLevel={2} appearanceLevel={5}>
                        {__('Your recommendation is based on these details:')}
                    </Heading>
                    <p>
                        {recommendationOrder
                            .map<(string | React.ReactNode | null)[]>((step) => [
                                answers[step],
                                ' ',
                            ])
                            .filter(Boolean)}
                    </p>
                </Box>
            </Box>
        </>
    );
}
